const {
    useCallback,
    useEffect,
    useRef,
    useState
} = require('react');
const { recordTwilioRecoveryEvent } = require('utils/twilio-recovery-debug');

const internals = {};

internals.CHAT_LOAD_TIMEOUT_MS = 15000;

internals.withTimeout = (promise, timeoutMs = internals.CHAT_LOAD_TIMEOUT_MS) => new Promise((resolve, reject) => {

    const timeout = setTimeout(() => {

        const error = new Error('Twilio chat load timed out');

        error.code = 'TWILIO_CHAT_LOAD_TIMEOUT';
        reject(error);
    }, timeoutMs);

    Promise.resolve(promise).then((result) => {

        clearTimeout(timeout);
        resolve(result);
    }, (error) => {

        clearTimeout(timeout);
        reject(error);
    });
});

internals.getFetchedMessageCount = (result) => {

    if (!result) {
        return null;
    }

    if (Array.isArray(result)) {
        return result
            .map(internals.getFetchedMessageCount)
            .reduce((max, count) => count === null ? max : Math.max(max || 0, count), null);
    }

    const candidates = [
        result.items,
        result.result,
        result.payload?.items,
        result.payload?.result
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate.length;
        }
    }

    const messageEntities = result.entities?.messages || result.payload?.entities?.messages;

    if (messageEntities && typeof messageEntities === 'object') {
        return Object.keys(messageEntities).length;
    }

    return null;
};

internals.waitForNextFrame = () => new Promise((resolve) => {

    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(resolve);
        return;
    }

    setTimeout(resolve, 0);
});

const useChatLoading = ({
    canLoadChat,
    dependenciesReady,
    getCurrentListeningChannel,
    hasMessages,
    messages,
    onRequestMessages,
    requestMessages,
    showNotification,
    sid,
    startListeningChannel,
    stopListeningChannel
}) => {

    const latestMessages = useRef({ sid, hasMessages });
    latestMessages.current = { sid, hasMessages };

    const [requestState, setRequestState] = useState(() => ({
        sid,
        status: messages.length ? 'ready' : 'loading'
    }));
    const [retryVersion, setRetryVersion] = useState(0);
    const manualRetrySid = useRef(null);

    const status = requestState.sid === sid
        ? requestState.status
        : messages.length ? 'ready' : 'loading';

    useEffect(() => {

        if (messages.length) {
            setRequestState({ sid, status: 'ready' });
        }
    }, [messages.length, sid]);

    useEffect(() => {

        if (!canLoadChat) {
            setRequestState({ sid, status: 'loading' });
            return undefined;
        }

        let isMounted = true;
        const isManualRetry = manualRetrySid.current === sid;
        manualRetrySid.current = null;

        setRequestState({
            sid,
            status: messages.length ? 'ready' : 'loading'
        });

        const requestInitialMessages = async ({ recover = false } = {}) => {

            let messageResult;

            if (recover) {
                await startListeningChannel(sid, { recover: true });
                messageResult = onRequestMessages ? await requestMessages({ initial: true }) : null;
            }
            else {
                const [, result] = await Promise.all([
                    sid !== getCurrentListeningChannel() ? startListeningChannel(sid) : null,
                    onRequestMessages ? requestMessages({ initial: true }) : null
                ]);

                messageResult = result;
            }

            if (onRequestMessages && typeof messageResult === 'undefined') {
                const error = new Error('Twilio message request failed');

                error.code = 'TWILIO_MESSAGE_REQUEST_FAILED';
                throw error;
            }

            return messageResult;
        };

        const initializeChat = async () => {

            const loadStartedAt = Date.now();

            recordTwilioRecoveryEvent('load_started', {
                attempt: retryVersion + 1,
                channelSid: sid,
                mode: isManualRetry ? 'manual' : 'initial'
            });

            try {
                let messageResult;
                let recovered = isManualRetry;

                try {
                    // A manual retry must abandon a cached, possibly stalled request.
                    messageResult = await internals.withTimeout(requestInitialMessages({ recover: recovered }));
                }
                catch (initialError) {
                    if (!isMounted) {
                        return;
                    }

                    recordTwilioRecoveryEvent(
                        initialError?.code === 'TWILIO_CHAT_LOAD_TIMEOUT' ? 'load_timeout' : 'load_failed',
                        {
                            channelSid: sid,
                            elapsedMs: Date.now() - loadStartedAt,
                            error: initialError
                        }
                    );
                    recordTwilioRecoveryEvent('recovery_started', {
                        channelSid: sid
                    });

                    console.warn('[chat] initial Twilio load failed; attempting transport recovery', {
                        sid,
                        error: initialError
                    });

                    messageResult = await internals.withTimeout(requestInitialMessages({
                        recover: true
                    }));
                    recovered = true;
                }

                const fetchedMessageCount = internals.getFetchedMessageCount(messageResult);

                if (fetchedMessageCount === null) {
                    await internals.waitForNextFrame();
                }

                if (isMounted) {
                    recordTwilioRecoveryEvent(recovered ? 'recovery_succeeded' : 'load_succeeded', {
                        channelSid: sid,
                        elapsedMs: Date.now() - loadStartedAt
                    });

                    if (recovered && !latestMessages.current.hasMessages) {
                        showNotification('Chat connection restored.');
                    }

                    setRequestState({
                        sid,
                        status: latestMessages.current.hasMessages || !fetchedMessageCount ? 'ready' : 'loading'
                    });
                }
            }
            catch (err) {
                if (!isMounted) {
                    return;
                }

                if (latestMessages.current.sid === sid && latestMessages.current.hasMessages) {
                    setRequestState({ sid, status: 'ready' });
                    return;
                }

                recordTwilioRecoveryEvent('recovery_failed', {
                    channelSid: sid,
                    elapsedMs: Date.now() - loadStartedAt,
                    error: err
                });

                console.error('[chat] failed to resolve initial messages', {
                    sid,
                    error: err
                });

                showNotification('Failed to load messages. Please try again.');

                if (isMounted) {
                    setRequestState({ sid, status: 'failed' });
                }
            }
        };

        initializeChat();

        return () => {
            isMounted = false;
            stopListeningChannel();
        };
    }, [canLoadChat, retryVersion, sid]);

    const retry = useCallback(() => {

        recordTwilioRecoveryEvent('manual_retry', { channelSid: sid });

        manualRetrySid.current = sid;
        setRequestState({ sid, status: 'loading' });
        setRetryVersion((version) => version + 1);
    }, [sid]);

    return {
        isResolving: !dependenciesReady || (!hasMessages && status === 'loading'),
        retry,
        showEmpty: dependenciesReady && !hasMessages && status === 'ready',
        showError: dependenciesReady && !hasMessages && status === 'failed',
        showMessages: dependenciesReady && hasMessages
    };
};

exports.useChatLoading = useChatLoading;

if (process.env.NODE_ENV === 'test') {
    exports._internals = internals;
}
